import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, PLATFORM_ID, ViewChild, inject } from '@angular/core';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import {
  HostListingDraftService, IDraftListing,
} from '@cnt-workspace/data-access';

/**
 * Resume-draft-card — surfaces an in-progress host listing draft so the user
 * can pick up where they left off. Rendered on /hosting (host dashboard) and
 * /properties (manage listings). Shows progress + last-updated time +
 * Continue / Discard actions.
 *
 * Emits `discarded` so the parent page can refresh any computed view-models
 * that depend on the draft state (most pages don't need this, but the listings
 * index does so the row disappears).
 */
@Component({
  selector: 'cnt-resume-draft-card',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './resume-draft-card.component.html',
})
export class ResumeDraftCardComponent implements OnInit, OnDestroy {
  private drafts = inject(HostListingDraftService);
  private platformId = inject(PLATFORM_ID);

  /** Set this to render the card in "shelved" mode — Resume + Discard action
   *  row, jungle-green palette, draft data sourced from the input rather than
   *  the active in-flight draft. Leave unset for the default active mode. */
  @Input() shelvedDraft: IDraftListing | null = null;
  /** Title of the listing this shelved draft was copied from — drives the
   *  "↳ Copied from X" breadcrumb on multi-copy shelves. Resolved by the
   *  parent via a HostListingsService.list() lookup; empty when the lineage
   *  doesn't resolve (or the draft was a raw fork). */
  @Input() sourceTitle = '';

  /** Fires after the host discards the draft (active or shelved), so parent
   *  pages can refresh + the parent owns the actual discard side effect. */
  @Output() discarded = new EventEmitter<void>();
  /** Fires when the host taps "Save a copy" on the active card. */
  @Output() duplicate = new EventEmitter<void>();
  /** Fires when the host taps Resume on a shelved card — parent owns the
   *  swap-in side effect via HostListingDraftService.resumeShelvedDraftById. */
  @Output() resume = new EventEmitter<void>();
  /** Fires when an inline title edit commits on a shelved card. The parent
   *  routes the new title through HostListingDraftService.renameShelvedDraft. */
  @Output() renamed = new EventEmitter<string>();

  @ViewChild('titleInput') titleInputEl?: ElementRef<HTMLInputElement>;

  /** Inline-rename state for shelved cards only. */
  editingTitle = false;
  titleDraft = '';

  draft: IDraftListing | null = null;
  completion: { stepsDone: number; stepsTotal: number; pct: number; phasesDone: [boolean, boolean, boolean] } | null = null;
  private sub?: Subscription;

  ngOnInit(): void {
    if (this.shelvedDraft) {
      this.draft = this.shelvedDraft;
      this.completion = this.drafts.completionFor(this.shelvedDraft);
      return;
    }
    this.sub = this.drafts.draft$.subscribe(d => {
      this.draft = d;
      this.completion = this.drafts.completion;
    });
  }

  get currentPhase(): 1 | 2 | 3 { return this.draft?.currentPhase ?? 1; }

  /** Human-readable "Started N days/hours ago" timestamp. */
  get updatedLabel(): string {
    const iso = this.draft?.updatedAt;
    if (!iso) return '';
    const diff = Date.now() - Date.parse(iso);
    if (Number.isNaN(diff)) return '';
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    const weeks = Math.floor(days / 7);
    return `${weeks}w ago`;
  }

  discard(): void {
    if (isPlatformBrowser(this.platformId) && !confirm('Discard your draft? This can\'t be undone.')) return;
    // For shelved cards, the parent owns the actual remove side effect so it
    // can target the right id; the active card wipes the in-flight slot.
    if (!this.shelvedDraft) this.drafts.discardDraft();
    this.discarded.emit();
  }

  // ─────────────── Inline rename (shelved cards only) ───────────────

  startTitleEdit(): void {
    if (!this.shelvedDraft || !this.draft) return;
    this.titleDraft = this.draft.title ?? '';
    this.editingTitle = true;
    queueMicrotask(() => {
      const el = this.titleInputEl?.nativeElement;
      if (!el) return;
      el.focus();
      el.select();
    });
  }

  commitTitleEdit(): void {
    if (!this.editingTitle) return;
    const trimmed = this.titleDraft.trim();
    if (trimmed && this.draft && trimmed !== this.draft.title) {
      this.renamed.emit(trimmed);
      this.draft = { ...this.draft, title: trimmed };
    }
    this.editingTitle = false;
  }

  cancelTitleEdit(): void {
    this.editingTitle = false;
    this.titleDraft = '';
  }

  // ─────────────── Age (shelved cards only) ───────────────

  /** True when the shelved draft hasn't been updated in 30+ days — drives
   *  the muted "Stale" chip next to the eyebrow. */
  get isStale(): boolean {
    return this.daysSinceUpdate >= 30;
  }
  /** True at 60+ days — drives the soft "still need this copy?" prompt
   *  inside the card body. */
  get isAncient(): boolean {
    return this.daysSinceUpdate >= 60;
  }
  private get daysSinceUpdate(): number {
    const iso = this.draft?.updatedAt;
    if (!iso) return 0;
    const diff = Date.now() - Date.parse(iso);
    if (Number.isNaN(diff) || diff < 0) return 0;
    return Math.floor(diff / 86_400_000);
  }

  ngOnDestroy(): void { this.sub?.unsubscribe(); }
}
